import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { PasswordInput } from "@/components/student/PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shell =
  "min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-[#020617] px-6 gradient-mesh";
const card =
  "w-full max-w-md rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] glass p-8 hairline";
const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/50 text-[#0F172A] dark:text-white";
const submit =
  "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A] px-5 py-3 text-sm font-semibold disabled:opacity-60";

const detailOf = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { email });
      setSent(true);
      toast.success("If that email is an admin, a reset link is on its way");
    } catch (err) {
      toast.error(detailOf(err, "Could not send the reset link"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-forgot-page" className={shell}>
      <div className={card}>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]">
            <Lock className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              SkillPreneur Academy
            </div>
            <div className="font-display text-xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
              Reset admin password
            </div>
          </div>
        </div>

        {sent ? (
          <div data-testid="admin-forgot-sent" className="mt-6 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              If <span className="font-medium">{email}</span> belongs to an admin, a single-use reset
              link valid for 1 hour is on its way. Check spam too.
            </p>
            <Link
              to="/admin/login"
              data-testid="admin-forgot-back"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] dark:text-[#06B6D4]"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to admin login
            </Link>
          </div>
        ) : (
          <form onSubmit={go} data-testid="admin-forgot-form" className="mt-6 space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Enter your admin email and we'll send you a link to set a new password.
            </p>
            <input
              data-testid="admin-forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={input}
            />
            <button type="submit" disabled={loading} data-testid="admin-forgot-submit" className={submit}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Send reset link
            </button>
            <Link
              to="/admin/login"
              data-testid="admin-forgot-back"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#2563EB]"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to login
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}

export function AdminResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, password: form.password });
      toast.success("Password updated — please sign in");
      nav("/admin/login", { replace: true });
    } catch (err) {
      toast.error(detailOf(err, "Could not reset the password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-reset-page" className={shell}>
      <div className={card}>
        <div className="font-display text-xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
          Set a new admin password
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Minimum 8 characters, with at least 1 uppercase letter and 1 number.
        </p>
        <form onSubmit={go} data-testid="admin-reset-form" className="mt-5 space-y-3">
          <PasswordInput
            testid="admin-reset-password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="New password"
            className={input}
          />
          <PasswordInput
            testid="admin-reset-confirm"
            required
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            placeholder="Confirm new password"
            className={input}
          />
          <button type="submit" disabled={loading || !token} data-testid="admin-reset-submit" className={submit}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Update password
          </button>
          {!token && (
            <p data-testid="admin-reset-no-token" className="text-xs text-rose-500">
              This link is missing its reset code. Request a new one from the login page.
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
